import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import type { Marker, MarkerKind, PlayStep, Position } from "../types";
import { ownerBallPosition, playerLabel } from "../lib/play";

interface CourtBoardProps {
  step: PlayStep;
  courtType: "half" | "full";
  interactive?: boolean;
  tool?: "select" | "ball" | MarkerKind;
  selectedMarkerId?: string | null;
  selectedPlayerId?: string | null;
  onPlayerMove?: (id: string, position: Position) => void;
  onPlayerSelect?: (id: string | null) => void;
  onBallMove?: (position: Position) => void;
  onBallOwnerChange?: (id: string | null) => void;
  onMarkerAdd?: (marker: Marker) => void;
  onMarkerSelect?: (id: string) => void;
  compact?: boolean;
}

interface DragState {
  kind: "player" | "ball";
  id?: string;
  position: Position;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function ArrowMarker({ marker, selected, onSelect }: { marker: Marker; selected: boolean; onSelect?: () => void }) {
  const color = marker.kind === "pass" ? "#f7cc66" : marker.kind === "screen" ? "#ee754f" : "#f1f6f3";
  if (marker.kind === "screen") {
    const angle = Math.atan2(marker.to.y - marker.from.y, marker.to.x - marker.from.x);
    const dx = Math.cos(angle + Math.PI / 2) * 3.5;
    const dy = Math.sin(angle + Math.PI / 2) * 3.5;
    return (
      <g className={onSelect ? "marker selectable" : "marker"} onPointerDown={(event) => { event.stopPropagation(); onSelect?.(); }}>
        <line x1={marker.from.x} y1={marker.from.y} x2={marker.to.x} y2={marker.to.y} stroke="transparent" strokeWidth="7" />
        <line x1={marker.from.x} y1={marker.from.y} x2={marker.to.x} y2={marker.to.y} stroke={color} strokeWidth={selected ? 1.8 : 1.2} />
        <line x1={marker.to.x - dx} y1={marker.to.y - dy} x2={marker.to.x + dx} y2={marker.to.y + dy} stroke={color} strokeWidth="1.5" />
      </g>
    );
  }
  return (
    <g className={onSelect ? "marker selectable" : "marker"} onPointerDown={(event) => { event.stopPropagation(); onSelect?.(); }}>
      <line x1={marker.from.x} y1={marker.from.y} x2={marker.to.x} y2={marker.to.y} stroke="transparent" strokeWidth="7" />
      <line
        x1={marker.from.x} y1={marker.from.y} x2={marker.to.x} y2={marker.to.y}
        stroke={color} strokeWidth={selected ? 1.8 : 1.2}
        strokeDasharray={marker.kind === "pass" ? "2.5 2.5" : undefined}
        markerEnd={`url(#arrow-${marker.kind})`}
      />
    </g>
  );
}

export function CourtBoard({
  step, courtType, interactive = false, tool = "select", selectedMarkerId, selectedPlayerId,
  onPlayerMove, onPlayerSelect, onBallMove, onBallOwnerChange, onMarkerAdd, onMarkerSelect, compact = false,
}: CourtBoardProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const height = courtType === "full" ? 150 : 75;
  const [drag, setDrag] = useState<DragState | null>(null);
  const [markerStart, setMarkerStart] = useState<Position | null>(null);
  const [markerEnd, setMarkerEnd] = useState<Position | null>(null);

  useEffect(() => {
    setDrag(null);
    setMarkerStart(null);
    setMarkerEnd(null);
  }, [step.id, tool]);

  const toCourtPoint = (clientX: number, clientY: number): Position => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const matrix = svg.getScreenCTM();
    const result = matrix ? point.matrixTransform(matrix.inverse()) : point;
    return { x: clamp(result.x, 3, 97), y: clamp(result.y, 3, height - 3) };
  };

  const ball = useMemo(() => ownerBallPosition(step), [step]);
  const renderedPlayers = step.players.map((player) =>
    drag?.kind === "player" && drag.id === player.id ? { ...player, ...drag.position } : player,
  );
  const renderedBall = drag?.kind === "ball" ? drag.position : ball;

  const commitDrag = () => {
    if (!drag) return;
    if (drag.kind === "player" && drag.id) onPlayerMove?.(drag.id, drag.position);
    if (drag.kind === "ball") onBallMove?.(drag.position);
    setDrag(null);
  };

  const finishMarker = () => {
    if (markerStart && markerEnd && ["run", "pass", "screen"].includes(tool)) {
      const distance = Math.hypot(markerEnd.x - markerStart.x, markerEnd.y - markerStart.y);
      if (distance > 3) onMarkerAdd?.({ id: crypto.randomUUID(), kind: tool as MarkerKind, from: markerStart, to: markerEnd });
    }
    setMarkerStart(null);
    setMarkerEnd(null);
  };

  return (
    <div className={`court-shell ${compact ? "compact" : ""}`}>
      <svg
        ref={svgRef}
        className={`court ${interactive ? "interactive" : ""} tool-${tool}`}
        viewBox={`0 0 100 ${height}`}
        role="img"
        aria-label={`${courtType === "half" ? "半场" : "全场"}战术图`}
        onPointerMove={(event) => {
          if (drag) setDrag({ ...drag, position: toCourtPoint(event.clientX, event.clientY) });
          if (markerStart) setMarkerEnd(toCourtPoint(event.clientX, event.clientY));
        }}
        onPointerUp={() => { commitDrag(); finishMarker(); }}
        onPointerCancel={() => { setDrag(null); setMarkerStart(null); setMarkerEnd(null); }}
        onPointerLeave={() => { if (drag) commitDrag(); if (markerStart) finishMarker(); }}
        onPointerDown={(event) => {
          if (!interactive) return;
          if (tool === "select") {
            onPlayerSelect?.(null);
            return;
          }
          if (!["run", "pass", "screen"].includes(tool)) return;
          const point = toCourtPoint(event.clientX, event.clientY);
          setMarkerStart(point);
          setMarkerEnd(point);
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
      >
        <defs>
          {(["run", "pass"] as const).map((kind) => (
            <marker key={kind} id={`arrow-${kind}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={kind === "pass" ? "#f7cc66" : "#f1f6f3"} />
            </marker>
          ))}
          <pattern id="court-grain" width="5" height="5" patternUnits="userSpaceOnUse">
            <path d="M0 0h5v5" fill="none" stroke="rgba(255,255,255,.025)" strokeWidth=".25" />
          </pattern>
        </defs>
        <rect width="100" height={height} rx="1.5" className="court-floor" />
        <rect width="100" height={height} rx="1.5" fill="url(#court-grain)" />
        <g className="court-lines">
          {courtType === "half" ? (
            <>
              <line x1="0" y1="74.5" x2="100" y2="74.5" />
              <circle cx="50" cy="75" r="12" />
              <rect x="34" y="0" width="32" height="28" />
              <path d="M34 28 A20 20 0 0 0 66 28" />
              <circle cx="50" cy="5.5" r="1.2" className="rim" />
              <line x1="43" y1="3" x2="57" y2="3" />
              <path d="M8 0 V11 A43 43 0 0 0 92 11 V0" />
            </>
          ) : (
            <>
              <line x1="0" y1="75" x2="100" y2="75" />
              <circle cx="50" cy="75" r="12" />
              <rect x="34" y="0" width="32" height="28" />
              <rect x="34" y="122" width="32" height="28" />
              <circle cx="50" cy="5.5" r="1.2" className="rim" />
              <circle cx="50" cy="144.5" r="1.2" className="rim" />
              <line x1="43" y1="3" x2="57" y2="3" />
              <line x1="43" y1="147" x2="57" y2="147" />
              <path d="M8 0 V11 A43 43 0 0 0 92 11 V0" />
              <path d="M8 150 V139 A43 43 0 0 1 92 139 V150" />
              <circle cx="50" cy="28" r="12" />
              <circle cx="50" cy="122" r="12" />
            </>
          )}
        </g>

        {step.markers.map((marker) => (
          <ArrowMarker key={marker.id} marker={marker} selected={selectedMarkerId === marker.id} onSelect={interactive ? () => onMarkerSelect?.(marker.id) : undefined} />
        ))}
        {markerStart && markerEnd && (
          <ArrowMarker marker={{ id: "draft", kind: tool as MarkerKind, from: markerStart, to: markerEnd }} selected={false} />
        )}

        <g
          className={`ball ${interactive ? "draggable" : ""}`}
          transform={`translate(${renderedBall.x} ${renderedBall.y})`}
          onPointerDown={(event) => {
            if (!interactive || tool !== "select") return;
            event.stopPropagation();
            onPlayerSelect?.(null);
            onBallOwnerChange?.(null);
            setDrag({ kind: "ball", position: renderedBall });
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
        >
          <circle r="2.05" />
          <path d="M-2 0h4M0-2a3 3 0 0 1 0 4M0-2a3 3 0 0 0 0 4" />
        </g>

        {renderedPlayers.filter((player) => player.visible).map((player) => {
          const label = playerLabel(player);
          const hasCustomColor = player.side === "offense" && Boolean(player.color);
          const style = hasCustomColor ? { "--player-color": player.color } as CSSProperties : undefined;
          return (
            <g
              key={player.id}
              className={`player ${player.side} ${step.ballOwnerId === player.id ? "owner" : ""} ${selectedPlayerId === player.id ? "selected" : ""} ${hasCustomColor ? "custom-color" : ""} ${player.side === "offense" && label.length > 1 ? "long-label" : ""} ${interactive ? "draggable" : ""}`}
              style={style}
              transform={`translate(${player.x} ${player.y})`}
              onPointerDown={(event) => {
                if (!interactive) return;
                if (["run", "pass", "screen"].includes(tool)) return;
                event.stopPropagation();
                if (tool === "ball" && player.side === "offense") {
                  onBallOwnerChange?.(player.id);
                  return;
                }
                if (tool !== "select") return;
                onPlayerSelect?.(player.side === "offense" ? player.id : null);
                setDrag({ kind: "player", id: player.id, position: { x: player.x, y: player.y } });
                event.currentTarget.setPointerCapture(event.pointerId);
              }}
            >
              {player.side === "offense" ? (
                <>
                  {step.ballOwnerId === player.id && <circle className="owner-ring" r="5.15" />}
                  {selectedPlayerId === player.id && <circle className="selection-ring" r="5.8" />}
                  <circle className="player-disc" r="4.2" />
                </>
              ) : (
                <path
                  className="defense-shield"
                  d="M0-4.4 3.7-3l-.3 3.8C3.1 2.6 1.8 3.8 0 4.7-1.8 3.8-3.1 2.6-3.4.8L-3.7-3Z"
                />
              )}
              <text y=".25" dominantBaseline="middle" textAnchor="middle">{player.side === "offense" ? label : player.number}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
